import {
  useEditAbacUserMutation,
  useResetUserPasswordMutation,
} from "../../AbacAppQueries";
import { useCallback, useState } from "react";
import { object, string } from "yup";
import { useFormik } from "formik";
import { useNotification } from "../../../../App/State/Hooks/useNotificationHooks";
import { IAbacUser, IEditAbacUser } from "../../../../App/Typings/AbacUser";

export default function useUserDetailsState(currentUser: IAbacUser) {
  const successMessage = `Successfully updated ${currentUser.firstName}'s information!`;

  const { mutate: editAbacUser, isLoading: editIsLoading } =
    useEditAbacUserMutation(currentUser.id);

  const { mutate: resetUserPassword, isLoading: resetIsLoading } =
    useResetUserPasswordMutation();
  const { displaySuccess, displayError } = useNotification();

  const [isActive, setIsActive] = useState<boolean>(currentUser.isActive);

  //Validation for the edit User form
  const validationSchema = object().shape({
    firstName: string()
      .trim()
      .max(50, "First Name must be less than 50 characters")
      .required("First Name is required"),
    lastName: string()
      .trim()
      .max(50, "Last Name must be less than 50 characters")
      .required("Last Name is required"),
    emailAddress: string()
      .trim()
      .max(50, "Email Address must be less than 50 characters")
      .required("Email Address is required"),
    phoneNumber: string()
      .trim()
      .max(50, "Phone Number must be less than 50 characters"),
  });

  //Initial edit User formik setup
  const formik = useFormik({
    initialValues: {
      firstName: currentUser.firstName ?? "",
      lastName: currentUser.lastName ?? "",
      emailAddress: currentUser.emailAddress ?? "",
      phoneNumber: currentUser.phoneNumber ?? "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let editedUser: IEditAbacUser = {
        id: currentUser.id,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        emailAddress: values.emailAddress.trim(),
        phoneNumber: values.phoneNumber.trim(),
        isActive: isActive,
      };
      editUser(editedUser);
    },
  });

  //Call the ABAC API endpoint for resetting a User's Auth0 password
  const handleResetPassword = useCallback(
    function resetPassword() {
      if (!resetIsLoading) {
        resetUserPassword(currentUser.emailAddress, {
          onError: () => {
            displayError("Error resetting password!");
          },
          onSuccess: () => {
            displaySuccess(`Reset email sent to ${currentUser.emailAddress}!`);
          },
        });
      }
    },
    [
      currentUser.emailAddress,
      displayError,
      displaySuccess,
      resetIsLoading,
      resetUserPassword,
    ]
  );

  // Edit the ABAC User
  const editUser = useCallback(
    function submitUser(editedAbacUser: IEditAbacUser) {
      if (!editIsLoading) {
        editAbacUser(editedAbacUser, {
          onSuccess: () => {
            displaySuccess(successMessage);
          },
        });
      }
    },
    [editIsLoading, editAbacUser, displayError, displaySuccess, successMessage]
  );

  return {
    getters: {
      isActive,
      formik,
    },
    setters: {
      setIsActive,
    },
    handlers: {
      handleResetPassword,
    },
  };
}
